import DocumentLink from '../../v2/documents/DocumentLink'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Maximize2, Minimize2, Check, X } from 'lucide-react'
import ReactFlow, {
  Background,
  Controls,
  ReactFlowProvider,
  useReactFlow,
  useNodesInitialized,
  Handle,
  Position as RFPosition,
  type Node,
  type Edge,
  type NodeProps,
} from 'reactflow'
import 'reactflow/dist/style.css'
import ExcerptStrips from '../pdf/ExcerptStrips'

// ---------------------------------------------------------------------------
// Granular chain graph (Ale 2026-06-22). Every primitive is its OWN node:
//   holder entity → position → instrument → issuer entity
// Shape encodes type (box / circle / hexagon), colour encodes review state
// (grey in-record · teal new · gold updated). Many-to-many converges (several
// positions on one instrument; several instruments on one issuer). Clicking a
// node opens a detail panel with facts, state, actions and a trades button.
// ---------------------------------------------------------------------------

export interface ChainObject {
  identity: string
  kind: string
  proposal_type: string
  label: string
  facts: Record<string, unknown>
  documents: { document_id: string; filename: string }[]
  proposal_ids: string[]
  confidence?: number | null
}

type ReviewState = 'new' | 'updated' | 'record'

const STATE_STYLE: Record<ReviewState, { border: string; bg: string }> = {
  new: { border: '#35e5c6', bg: '#ecfdf5' },
  updated: { border: '#a98410', bg: '#fef9ec' },
  record: { border: '#9ca3af', bg: '#ffffff' },
}

// --- fact accessors -------------------------------------------------------
function factStr(o: ChainObject, k: string): string | null {
  const v = o.facts[k]
  return typeof v === 'string' && v ? v : null
}
function factNum(o: ChainObject, k: string): number | null {
  const v = o.facts[k]
  if (typeof v === 'number') return v
  if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) return Number(v)
  return null
}
function dedupKey(o: ChainObject): string {
  const v = o.facts['dedup_key']
  return typeof v === 'string' ? v : ''
}
function stateOf(o: ChainObject): ReviewState {
  return o.proposal_type?.startsWith('update') ? 'updated' : 'new'
}

// position dedup_key: position:<issuerId>:<instrName>:<holderId>:<role>
function positionParts(o: ChainObject) {
  const p = dedupKey(o).split(':')
  return {
    issuerId: (p[0] === 'position' ? p[1] : '') || factStr(o, 'issuer_entity_id') || '',
    instrName: (p[0] === 'position' ? p[2] : '') || factStr(o, 'instrument_name') || '',
    holderId: (p[0] === 'position' ? p[3] : '') || factStr(o, 'holder_entity_id') || '',
    role: (p[0] === 'position' ? p[4] : '') || factStr(o, 'position_role') || 'single',
  }
}
function entityId(o: ChainObject): string {
  const p = dedupKey(o).split(':')
  if (p[0] === 'entity' && p[1]) return p[1]
  return factStr(o, 'entity_id') || o.identity
}
function instrumentKey(issuerId: string, name: string): string {
  return `${issuerId}::${name.trim().toLowerCase()}`
}

const CCY: Record<string, string> = { USD: '$', EUR: '€', GBP: '£', CHF: 'CHF ' }
function fmtCapital(units: number | null, ccy: string | null): string | null {
  if (units == null || !ccy) return null // capital only when a currency is attached
  const sym = CCY[ccy] ?? `${ccy} `
  const abs = Math.abs(units)
  const sign = units < 0 ? '−' : ''
  let body: string
  if (abs >= 1e9) body = `${(abs / 1e9).toFixed(abs >= 1e10 ? 0 : 1)}B`
  else if (abs >= 1e6) body = `${(abs / 1e6).toFixed(abs >= 1e7 ? 0 : 1)}M`
  else if (abs >= 1e3) body = `${Math.round(abs / 1e3)}K`
  else body = `${abs}`
  return `${sign}${sym}${body}`
}

// --- node data shapes -----------------------------------------------------
interface ENode {
  name: string
  nature: string | null
  state: ReviewState
  isFund: boolean
  ident: string | null
}
interface PNode {
  capital: string | null
  pct: number | null
  units: number | null
  role: string
  state: ReviewState
  ident: string
}
interface INode {
  name: string
  itype: string | null
  issued: number | null
  state: ReviewState
  ident: string | null
}

// ===========================================================================
// Graph construction
// ===========================================================================
function buildGraph(objects: ChainObject[], recordIds: Set<string>) {
  const entities = objects.filter((o) => o.kind === 'Entity' || o.kind === 'Fund')
  const positions = objects.filter((o) => o.kind === 'Position')
  const instruments = objects.filter((o) => o.kind === 'Instrument')

  const objById = new Map<string, ChainObject>() // node id → owning object (for the panel)

  // entity nodes (from objects, then placeholders for referenced ids)
  const eNodes = new Map<string, ENode>()
  const nameById = new Map<string, string>()
  for (const e of entities) {
    const id = entityId(e)
    eNodes.set(id, {
      name: e.label,
      nature: factStr(e, 'entity_nature') || factStr(e, 'nature'),
      state: stateOf(e),
      isFund: e.kind === 'Fund' || Boolean(e.facts['is_fund_root']),
      ident: e.identity,
    })
    objById.set(`e:${id}`, e)
    nameById.set(id, e.label)
  }
  for (const p of positions) {
    const { holderId, issuerId } = positionParts(p)
    const hn = factStr(p, 'holder_entity_name')
    const isn = factStr(p, 'issuer_entity_name')
    if (holderId && hn) nameById.set(holderId, hn)
    if (issuerId && isn) nameById.set(issuerId, isn)
  }
  const ensureEntity = (id: string) => {
    if (!id || eNodes.has(id)) return
    eNodes.set(id, {
      name: nameById.get(id) || id.slice(0, 8),
      nature: null,
      state: recordIds.has(id) ? 'record' : 'new',
      isFund: false,
      ident: null,
    })
  }

  // instrument nodes
  const iNodes = new Map<string, INode>()
  for (const ins of instruments) {
    const issuerId = factStr(ins, 'issuer_entity_id') || ''
    const name = factStr(ins, 'instrument_name') || ins.label
    const key = instrumentKey(issuerId, name)
    iNodes.set(key, {
      name,
      itype: factStr(ins, 'category') || (ins.facts['is_fictitious'] ? 'fictitious' : null),
      issued: factNum(ins, 'total_issued'),
      state: stateOf(ins),
      ident: ins.identity,
    })
    objById.set(`i:${key}`, ins)
    ensureEntity(issuerId)
  }

  // position nodes + edges
  type PosRec = { id: string; holderId: string; issuerId: string; instrKey: string; data: PNode }
  const posRecs: PosRec[] = []
  for (const p of positions) {
    const { holderId, issuerId, instrName, role } = positionParts(p)
    const instrKey = instrumentKey(issuerId, instrName)
    if (!iNodes.has(instrKey)) {
      // placeholder instrument referenced by a position with no instrument object
      iNodes.set(instrKey, {
        name: instrName || 'instrument',
        itype: null,
        issued: null,
        state: recordIds.has(issuerId) ? 'record' : 'new',
        ident: null,
      })
    }
    ensureEntity(holderId)
    ensureEntity(issuerId)
    const pid = `p:${p.identity}`
    // Ownership % is DERIVED, never read from a stored field: units held in the
    // position ÷ units issued by the instrument (Ale 2026-06-22).
    const held = factNum(p, 'units_held')
    const issued = iNodes.get(instrKey)?.issued ?? null
    const derivedPct =
      held != null && issued != null && issued > 0 ? Math.round((held / issued) * 1000) / 10 : null
    posRecs.push({
      id: pid,
      holderId,
      issuerId,
      instrKey,
      data: {
        capital: fmtCapital(held, factStr(p, 'units_currency')),
        pct: derivedPct,
        units: held,
        role: role === 'single' ? '' : role,
        state: stateOf(p),
        ident: p.identity,
      },
    })
    objById.set(pid, p)
  }

  // --- entity depth (longest path from roots, holder shallower than issuer) ---
  const eIds = [...eNodes.keys()]
  const incoming = new Map<string, string[]>()
  const e2e: [string, string][] = []
  for (const r of posRecs) {
    if (r.holderId && r.issuerId && r.holderId !== r.issuerId) {
      e2e.push([r.holderId, r.issuerId])
      ;(incoming.get(r.issuerId) ?? incoming.set(r.issuerId, []).get(r.issuerId)!).push(r.holderId)
    }
  }
  const depth = new Map<string, number>()
  eIds.forEach((id) => {
    if (eNodes.get(id)!.isFund || !incoming.has(id)) depth.set(id, 0)
  })
  if (depth.size === 0 && eIds.length) depth.set(eIds[0], 0)
  for (let pass = 0; pass < eIds.length + 2; pass++) {
    let changed = false
    for (const [h, i] of e2e) {
      const dh = depth.get(h)
      if (dh === undefined) continue
      if ((depth.get(i) ?? -1) < dh + 1) {
        depth.set(i, dh + 1)
        changed = true
      }
    }
    if (!changed) break
  }
  eIds.forEach((id) => {
    if (!depth.has(id)) depth.set(id, 0)
  })

  // --- x placement: entities per layer (barycentre), then derive others -----
  const LVL = 280,
    COL = 230
  const byLayer = new Map<number, string[]>()
  for (const id of eIds)
    (byLayer.get(depth.get(id)!) ?? byLayer.set(depth.get(id)!, []).get(depth.get(id)!)!).push(id)
  const ex = new Map<string, number>()
  for (const d of [...byLayer.keys()].sort((a, b) => a - b)) {
    const layer = byLayer.get(d)!
    const bary = (id: string) => {
      const hs = (incoming.get(id) ?? [])
        .map((h) => ex.get(h))
        .filter((x): x is number => x !== undefined)
      return hs.length ? hs.reduce((a, b) => a + b, 0) / hs.length : Number.POSITIVE_INFINITY
    }
    layer.sort(
      (a, b) => bary(a) - bary(b) || eNodes.get(a)!.name.localeCompare(eNodes.get(b)!.name)
    )
    layer.forEach((id, k) => ex.set(id, (k - (layer.length - 1) / 2) * COL))
  }

  // instruments: spread among the instruments that share an issuer, above it
  const instrByIssuer = new Map<string, string[]>()
  for (const [key, n] of iNodes) {
    const issuerId = key.split('::')[0]
    void n
    ;(instrByIssuer.get(issuerId) ?? instrByIssuer.set(issuerId, []).get(issuerId)!).push(key)
  }
  const ix = new Map<string, number>()
  const iy = new Map<string, number>()
  for (const [issuerId, keys] of instrByIssuer) {
    const base = ex.get(issuerId) ?? 0
    const dIss = depth.get(issuerId) ?? 1
    keys.forEach((key, k) => {
      ix.set(key, base + (k - (keys.length - 1) / 2) * (COL * 0.8))
      iy.set(key, dIss * LVL - 96)
    })
  }

  // positions: sit above their instrument (so siblings on different instruments
  // separate by instrument spacing); dual positions on ONE instrument fan.
  const posByInstr = new Map<string, string[]>()
  for (const r of posRecs)
    (posByInstr.get(r.instrKey) ?? posByInstr.set(r.instrKey, []).get(r.instrKey)!).push(r.id)
  const px = new Map<string, number>()
  const py = new Map<string, number>()
  for (const r of posRecs) {
    const insX = ix.get(r.instrKey) ?? ex.get(r.holderId) ?? 0
    const sib = posByInstr.get(r.instrKey)!
    const k = sib.indexOf(r.id)
    const fan = (k - (sib.length - 1) / 2) * 78
    px.set(r.id, insX + fan)
    py.set(r.id, (depth.get(r.holderId) ?? 0) * LVL + 96)
  }

  // --- collision pass: nothing may overlap ---------------------------------
  // Instruments are fanned around THEIR OWN issuer with no global check, so
  // two adjacent issuers' fans ran into each other and the balances became
  // physically unreadable (atlas 2026-07-28). Same for position legs whose
  // instruments sit close. Sweep each row left→right and push any node that
  // starts before the previous one ends. Order is preserved — only spacing
  // changes — so the barycentre layout above still decides who sits where.
  const separateRow = (xs: Map<string, number>, ids: string[], minGap: number) => {
    const ordered = [...ids].sort((a, b) => (xs.get(a) ?? 0) - (xs.get(b) ?? 0))
    for (let k = 1; k < ordered.length; k++) {
      const prev = xs.get(ordered[k - 1]) ?? 0
      const cur = xs.get(ordered[k]) ?? 0
      if (cur - prev < minGap) xs.set(ordered[k], prev + minGap)
    }
    // Re-centre the row on its original midpoint so the graph does not drift
    // right as rows get wider.
    if (ordered.length > 1) {
      const first = xs.get(ordered[0]) ?? 0
      const last = xs.get(ordered[ordered.length - 1]) ?? 0
      const shift = (first + last) / 2
      for (const id of ordered) xs.set(id, (xs.get(id) ?? 0) - shift)
    }
  }
  const INSTR_GAP = 184 // 168 node + 16 breathing room
  const POS_GAP = 78 // 66 node + 12
  const rowsOf = (ys: Map<string, number>, ids: string[]) => {
    const rows = new Map<number, string[]>()
    for (const id of ids) {
      const y = Math.round(ys.get(id) ?? 0)
      ;(rows.get(y) ?? rows.set(y, []).get(y)!).push(id)
    }
    return rows
  }
  for (const [, ids] of rowsOf(iy, [...iNodes.keys()])) separateRow(ix, ids, INSTR_GAP)
  for (const [, ids] of rowsOf(
    py,
    posRecs.map((r) => r.id)
  ))
    separateRow(px, ids, POS_GAP)

  // --- assemble ReactFlow nodes + edges ------------------------------------
  const NODE_W = 168
  const rfNodes: Node[] = []
  for (const [id, n] of eNodes)
    rfNodes.push({
      id: `e:${id}`,
      type: 'entity',
      position: { x: (ex.get(id) ?? 0) - NODE_W / 2, y: depth.get(id)! * LVL },
      data: n,
    })
  for (const [key, n] of iNodes)
    rfNodes.push({
      id: `i:${key}`,
      type: 'instrument',
      position: { x: (ix.get(key) ?? 0) - NODE_W / 2, y: iy.get(key) ?? 0 },
      data: n,
    })
  for (const r of posRecs)
    rfNodes.push({
      id: r.id,
      type: 'position',
      position: { x: (px.get(r.id) ?? 0) - 33, y: py.get(r.id) ?? 0 },
      data: r.data,
      zIndex: 5,
    })

  const rfEdges: Edge[] = []
  const nodeIds = new Set(rfNodes.map((n) => n.id))
  const seen = new Set<string>()
  const edge = (s: string, t: string) => {
    const id = `${s}->${t}`
    if (!seen.has(id) && nodeIds.has(s) && nodeIds.has(t)) {
      seen.add(id)
      rfEdges.push({
        id,
        source: s,
        target: t,
        type: 'smoothstep',
        style: { stroke: '#cbd5e1', strokeWidth: 1.4 },
      })
    }
  }
  for (const r of posRecs) {
    edge(`e:${r.holderId}`, r.id) // holder → position
    edge(r.id, `i:${r.instrKey}`) // position → instrument
  }
  // Instrument → issuer for EVERY instrument (not only those with positions),
  // else an instrument a fund issues with no recorded holder floats loose.
  for (const key of iNodes.keys()) {
    const issuerId = key.split('::')[0]
    if (issuerId) edge(`i:${key}`, `e:${issuerId}`)
  }

  return { rfNodes, rfEdges, objById }
}

// ===========================================================================
// Node components
// ===========================================================================
function EntityNode({ data, selected }: NodeProps<ENode>) {
  const s = STATE_STYLE[data.state]
  return (
    <div
      className="px-3 py-2 rounded-lg text-center"
      style={{
        width: 168,
        background: s.bg,
        border: `2px solid ${s.border}`,
        boxShadow: selected ? '0 0 0 2px #0c4fab' : 'var(--shadow-card)',
      }}
    >
      <Handle type="target" position={RFPosition.Top} style={{ opacity: 0 }} />
      <div className="text-[12px] font-semibold text-slate-900 leading-tight line-clamp-2">
        {data.name}
      </div>
      {data.nature && (
        <div className="text-[8.5px] text-slate-500 mt-0.5">{data.nature.replace(/_/g, ' ')}</div>
      )}
      {data.isFund && (
        <div className="text-[8px] font-bold uppercase tracking-wide text-slate-700 mt-0.5">
          Fund
        </div>
      )}
      <Handle type="source" position={RFPosition.Bottom} style={{ opacity: 0 }} />
    </div>
  )
}

function PositionNode({ data, selected }: NodeProps<PNode>) {
  const s = STATE_STYLE[data.state]
  const fmtU = (u: number) => (Math.abs(u) >= 1000 ? `${Math.round(u / 1000)}k` : `${u}`)
  // ALWAYS show units held (the currency amount when it is one, else the unit
  // count); add the derived % below where applicable (Ale 2026-06-22).
  const unitsLabel = data.capital ?? (data.units != null ? fmtU(data.units) : null)
  const pctLabel = data.pct != null ? `${data.pct}%` : null
  const head = unitsLabel ?? pctLabel ?? '—'
  const sub = unitsLabel && pctLabel ? pctLabel : null
  return (
    <div style={{ width: 66 }}>
      <Handle type="target" position={RFPosition.Top} style={{ opacity: 0 }} />
      <div
        className="rounded-full flex flex-col items-center justify-center text-center"
        style={{
          width: 66,
          height: 66,
          background: s.bg,
          border: `2px solid ${s.border}`,
          boxShadow: selected ? '0 0 0 2px #0c4fab' : '0 1px 3px rgba(15,23,42,0.08)',
        }}
      >
        <span className="text-[11px] font-bold text-slate-900 leading-none">{head}</span>
        {sub && <span className="text-[8px] text-slate-500 leading-none mt-0.5">{sub}</span>}
      </div>
      {data.role && (
        <div className="text-[8.5px] font-semibold text-slate-600 text-center mt-0.5 capitalize">
          {data.role}
        </div>
      )}
      <Handle type="source" position={RFPosition.Bottom} style={{ opacity: 0 }} />
    </div>
  )
}

function InstrumentNode({ data, selected }: NodeProps<INode>) {
  const s = STATE_STYLE[data.state]
  const sub = [
    data.itype ? data.itype.replace(/_/g, ' ') : null,
    data.issued != null
      ? `${data.issued.toLocaleString('en-GB')} unit${data.issued === 1 ? '' : 's'}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ')
  return (
    <div style={{ width: 168, height: 46, position: 'relative' }}>
      <Handle type="target" position={RFPosition.Top} style={{ opacity: 0 }} />
      <svg width="168" height="46" viewBox="0 0 168 46" style={{ position: 'absolute', inset: 0 }}>
        <polygon
          points="13,2 155,2 166,23 155,44 13,44 2,23"
          fill={s.bg}
          stroke={s.border}
          strokeWidth={selected ? 3 : 2}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
        <span className="text-[10.5px] font-semibold text-slate-900 leading-tight line-clamp-1">
          {data.name}
        </span>
        {sub && <span className="text-[8px] text-slate-500 leading-tight">{sub}</span>}
      </div>
      <Handle type="source" position={RFPosition.Bottom} style={{ opacity: 0 }} />
    </div>
  )
}

const NODE_TYPES = { entity: EntityNode, position: PositionNode, instrument: InstrumentNode }

// ===========================================================================
// Detail panel — facts, state, actions, trades
// ===========================================================================
const HIDE = new Set([
  'dedup_key',
  'source_coordinate',
  'depositary_id',
  'source',
  'relevance',
  'evidence_instructions',
  'entity_id',
  'instrument_id',
  'issuer_entity_id',
  'holder_entity_id',
  'position_id',
])
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function DetailPanel({
  obj,
  trades,
  busy,
  onClose,
  onDecide,
  onOpenDoc,
}: {
  obj: ChainObject
  trades: ChainObject[]
  busy: boolean
  onClose: () => void
  onDecide: (o: ChainObject, a: 'approve' | 'reject') => void
  onOpenDoc: (id: string) => void
}) {
  const [showTrades, setShowTrades] = useState(false)
  // Verify-at-source (diagram view): the tapped node's cited passage renders
  // inside this panel — same excerpt engine as the cards, canvas untouched.
  const sc = obj.facts['source_coordinate'] as { page?: unknown; text?: unknown } | undefined
  const excerptSources = obj.documents.slice(0, 1).map((d) => ({
    documentId: d.document_id,
    filename: d.filename,
    page: sc && typeof sc.page === 'number' ? sc.page : undefined,
    text: sc && typeof sc.text === 'string' && sc.text ? sc.text : undefined,
  }))
  const facts = Object.entries(obj.facts).filter(
    ([k, v]) =>
      !HIDE.has(k) &&
      !(typeof v === 'string' && UUID.test(v)) &&
      v !== null &&
      v !== '' &&
      !Array.isArray(v) &&
      typeof v !== 'object'
  )
  const kindLabel = obj.kind
  return (
    <div className="absolute top-3 right-3 w-80 max-h-[calc(100%-1.5rem)] overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg p-4 z-20">
      <div className="flex items-start justify-between gap-2 mb-1">
        <div>
          <div className="text-[9px] font-bold uppercase tracking-wide text-slate-400">
            {kindLabel}
          </div>
          <div className="text-sm font-semibold text-slate-900 leading-tight">{obj.label}</div>
        </div>
        <button
          onClick={onClose}
          className="text-slate-400 hover:text-slate-600 text-xs shrink-0"
          aria-label="Close"
        >
          ✕
        </button>
      </div>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-[11px] mt-3">
        {facts.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-slate-500 capitalize">{k.replace(/_/g, ' ')}</dt>
            <dd className="text-slate-900 font-medium break-words">{String(v)}</dd>
          </div>
        ))}
      </dl>

      {excerptSources.length > 0 && (
        <div className="mt-3">
          <ExcerptStrips sources={excerptSources} onOpenDoc={(id) => onOpenDoc(id)} />
        </div>
      )}

      {obj.documents.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-3">
          {obj.documents.map((d) => (
            <DocumentLink
              key={d.document_id}
              document={{ document_id: d.document_id }}
              className="inline-flex items-center gap-1 text-[10px] border border-slate-300 rounded px-1.5 py-0.5 text-slate-600 hover:border-nolos-primary hover:text-nolos-primary"
            />
          ))}
        </div>
      )}

      {(obj.kind === 'Position' || obj.kind === 'Instrument') && trades.length > 0 && (
        <div className="mt-3">
          <button
            onClick={() => setShowTrades((v) => !v)}
            className="text-[11px] font-semibold text-nolos-primary hover:underline"
          >
            {showTrades ? '▾' : '▸'} {trades.length} trade{trades.length === 1 ? '' : 's'}
          </button>
          {showTrades && (
            <div className="mt-1.5 space-y-1">
              {trades.map((t) => (
                <div
                  key={t.identity}
                  className="text-[10px] text-slate-600 border-l-2 border-slate-200 pl-2"
                >
                  {factStr(t, 'transaction_type') || factStr(t, 'type') || 'trade'} ·{' '}
                  {factStr(t, 'effective_date') || '—'} ·{' '}
                  {factNum(t, 'units_delta') ?? factNum(t, 'units') ?? '—'}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex gap-1.5 mt-4">
        <button
          onClick={() => onDecide(obj, 'approve')}
          disabled={busy}
          className="flex-1 flex items-center justify-center gap-1 text-[11px] font-medium px-2 py-1.5 rounded bg-emerald-600 text-white hover:opacity-90 disabled:opacity-40"
        >
          <Check size={12} /> Approve
        </button>
        <button
          onClick={() => onDecide(obj, 'reject')}
          disabled={busy}
          className="flex items-center justify-center gap-1 text-[11px] font-medium px-2 py-1.5 rounded border border-slate-300 text-nolos-warn hover:bg-slate-50 disabled:opacity-40"
        >
          <X size={12} /> Discard
        </button>
      </div>
    </div>
  )
}

// ===========================================================================
// Inner graph (needs ReactFlow context)
// ===========================================================================
function ChainGraphInner({
  objects,
  recordIds,
  busyId,
  height = 560,
  onDecide,
  onOpenDoc,
}: {
  objects: ChainObject[]
  recordIds: Set<string>
  busyId: string | null
  height?: number | string
  onDecide: (o: ChainObject, a: 'approve' | 'reject') => void
  onOpenDoc: (id: string) => void
}) {
  const [selId, setSelId] = useState<string | null>(null)
  const [isFull, setIsFull] = useState(false)
  const { fitView } = useReactFlow()
  const inited = useNodesInitialized()

  const { rfNodes, rfEdges, objById } = useMemo(
    () => buildGraph(objects, recordIds),
    [objects, recordIds]
  )

  useEffect(() => {
    if (!inited) return
    const t = window.setTimeout(() => fitView({ padding: 0.16, maxZoom: 1.1, duration: 200 }), 60)
    return () => window.clearTimeout(t)
  }, [inited, isFull, rfNodes, fitView])

  useEffect(() => {
    if (!isFull) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFull(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isFull])

  const onNodeClick = useCallback((_: unknown, node: Node) => setSelId(node.id), [])
  const selObj = selId ? (objById.get(selId) ?? null) : null
  const trades = useMemo(() => objects.filter((o) => o.kind === 'Trade'), [objects])

  const body = (
    <>
      <button
        type="button"
        onClick={() => setIsFull((f) => !f)}
        className="absolute top-2 left-2 z-30 flex items-center justify-center w-8 h-8 rounded-md bg-white/90 border border-slate-200 text-slate-500 hover:text-slate-800 shadow-sm"
        aria-label={isFull ? 'Exit full screen' : 'Full screen'}
        title={isFull ? 'Exit full screen (Esc)' : 'Full screen'}
      >
        {isFull ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
      </button>
      <ReactFlow
        nodes={rfNodes}
        edges={rfEdges}
        nodeTypes={NODE_TYPES}
        onNodeClick={onNodeClick}
        onPaneClick={() => setSelId(null)}
        fitView
        fitViewOptions={{ padding: 0.16, maxZoom: 1.1 }}
        minZoom={0.2}
        maxZoom={1.6}
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={16} color="#e2e8f0" />
        <Controls position="bottom-right" showInteractive={false} />
      </ReactFlow>

      {selObj && (
        <DetailPanel
          obj={selObj}
          trades={trades}
          busy={busyId === selObj.identity}
          onClose={() => setSelId(null)}
          onDecide={onDecide}
          onOpenDoc={onOpenDoc}
        />
      )}

      <div className="absolute bottom-3 left-3 z-20 bg-white/90 backdrop-blur border border-slate-200 rounded-md px-3 py-1.5 text-[10.5px] text-slate-600 shadow-sm flex flex-col gap-1">
        <div className="flex items-center gap-3">
          <span className="font-semibold text-slate-500">shape</span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-4 h-2.5 rounded-sm border border-slate-400" />
            entity
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-full border border-slate-400" />
            position
          </span>
          <span className="flex items-center gap-1">
            <span
              className="inline-block w-3.5 h-3"
              style={{
                clipPath: 'polygon(12% 0,88% 0,100% 50%,88% 100%,12% 100%,0 50%)',
                background: '#cbd5e1',
              }}
            />
            instrument
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-semibold text-slate-500">state</span>
          <span className="flex items-center gap-1">
            <span
              className="inline-block w-3 h-3 rounded-sm"
              style={{ background: '#fff', border: '1.5px solid #9ca3af' }}
            />
            in record
          </span>
          <span className="flex items-center gap-1">
            <span
              className="inline-block w-3 h-3 rounded-sm"
              style={{ background: '#ecfdf5', border: '1.5px solid #35e5c6' }}
            />
            new
          </span>
          <span className="flex items-center gap-1">
            <span
              className="inline-block w-3 h-3 rounded-sm"
              style={{ background: '#fef9ec', border: '1.5px solid #a98410' }}
            />
            updated
          </span>
        </div>
      </div>
    </>
  )

  if (isFull)
    return createPortal(
      <div className="fixed inset-0 z-[100] bg-white">
        <div className="relative w-full h-full overflow-hidden">{body}</div>
      </div>,
      document.body
    )
  return (
    <div
      style={{ height }}
      className="border border-slate-200 rounded-lg bg-white relative overflow-hidden"
    >
      {body}
    </div>
  )
}

export default function ChainGraph(props: {
  objects: ChainObject[]
  recordIds: Set<string>
  busyId: string | null
  height?: number | string
  onDecide: (o: ChainObject, a: 'approve' | 'reject') => void
  onOpenDoc: (id: string) => void
}) {
  return (
    <ReactFlowProvider>
      <ChainGraphInner {...props} />
    </ReactFlowProvider>
  )
}
